import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { ThemedButtonProps, ThemedLinkProps } from './types'
import {
  BarrySurfaceWithStateProps,
  barrySurface,
  customPadding,
  defaultTransition,
  paddingRules,
} from '../../theme'
import { getRgbaValue } from '../../utils/styling'

const buttonStyles = css<ThemedButtonProps>`
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  display: inline-flex;
  flex: unset;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${({ w }) => w ?? 'auto'};
  padding: ${({ p }) => p ?? '0 20px'};
  height: 44px;
  outline: none;
  border-radius: 48px;
  ${defaultTransition}
  ${({ theme, $styleType }) => {
    switch ($styleType) {
      case 'primary':
        return css`
          font-family: var(--type-family-space-grotesk);
          font-size: var(--Text-M-M-size);
          font-style: normal;
          font-weight: var(--type-weight-Semibold);
          line-height: var(--Text-M-M-line-height); /* 128.571% */
          gap: var(--units-8, 8px);
          border-radius: 48px;
          background: var(--surface-brand);
          color: var(--text-invert);
          font-weight: var(--type-weight-Semibold);

          :hover {
            background: linear-gradient(
                0deg,
                rgba(255, 255, 255, 0.4) 0%,
                rgba(255, 255, 255, 0.4) 100%
              ),
              var(--surface-brand, #ff0);
          }
          :disabled {
            // Don't apply this to all buttons. It doesn't look good on transparent backgrounds
            opacity: 0.5;
          }
        `
      case 'primary-invert':
        return css`
          font-family: var(--type-family-space-grotesk);
          font-size: var(--Text-L-L-size);
          font-style: normal;
          font-weight: var(--type-weight-Semibold);
          line-height: var(--Text-L-L-line-height); /* 128.571% */
          gap: var(--units-8, 8px);
          border-radius: 48px;
          background: var(--surface-brand-invert, #080808);
          color: var(--surface-brand);
          font-weight: var(--type-weight-Semibold);

          :hover {
            opacity: 0.9;
          }
          :disabled {
            // Don't apply this to all buttons. It doesn't look good on transparent backgrounds
            opacity: 0.5;
          }
        `
      case 'secondary':
        return css`
          display: inline-flex;
          padding: var(--units-12, 12px) var(--units-20, 20px);
          justify-content: center;
          align-items: center;
          gap: var(--units-8, 8px);
          border-radius: 48px;
          border: var(--units-1, 1px) solid var(--stroke-primary, #fff);
          color: var(--text-primary, #fff);
          text-align: center;
          /* Web/Text/M/Semibold */
          font-family: var(--type-family-space-grotesk, 'Space Grotesk');
          font-size: var(--Text-M-M-size, 14px);
          font-style: normal;
          font-weight: var(--type-weight-Semibold, 600);
          line-height: var(--Text-M-M-line-height, 18px); /* 128.571% */
          letter-spacing: -0.28px;
          :hover {
            background: var(
              --surface-minimal-primary,
              rgba(255, 255, 255, 0.06)
            );
          }
        `
      case 'secondary-error':
        return css`
          display: inline-flex;
          padding: var(--units-12, 12px) var(--units-20, 20px);
          justify-content: center;
          align-items: center;
          gap: var(--units-8, 8px);
          border-radius: 48px;
          border: var(--units-1, 1px) solid ${theme.colors.function.error};
          color: var(--text-primary, #fff);
          text-align: center;
          /* Web/Text/M/Semibold */
          font-family: var(--type-family-space-grotesk, 'Space Grotesk');
          font-size: var(--Text-M-M-size, 14px);
          font-style: normal;
          font-weight: var(--type-weight-Semibold, 600);
          line-height: var(--Text-M-M-line-height, 18px); /* 128.571% */
          letter-spacing: -0.28px;
          :hover {
            background: ${getRgbaValue(theme.colors.function.error, 0.2)};
          }
          :active {
            background: ${getRgbaValue(theme.colors.function.error, 0.3)};
          }
          :focus,
          :focus-visible {
            box-shadow: 0 0 2px 2px ${theme.colors.function.error};
          }
        `
      case 'tertiary':
        return css`
          display: inline-flex;
          padding: var(--units-12, 12px) var(--units-20, 20px);
          justify-content: center;
          align-items: center;
          gap: var(--units-8, 8px);
          border-radius: 48px;
          background: linear-gradient(
              0deg,
              var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%,
              var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04))
                100%
            ),
            var(--surface-primary, #080808);
          color: var(--text-primary, #fff);
          text-align: center;
          font-family: var(--type-family-space-grotesk, 'Space Grotesk');
          font-size: var(--Text-M-M-size, 14px);
          font-style: normal;
          font-weight: var(--type-weight-Semibold, 600);
          line-height: var(--Text-M-M-line-height, 18px); /* 128.571% */
          letter-spacing: -0.28px;
          border: 1px solid #141414;
          :hover {
            border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
            background: linear-gradient(
                0deg,
                var(--states-minimal-alpha-4, rgba(255, 255, 255, 0.04)) 0%,
                var(--states-minimal-alpha-4, rgba(255, 255, 255, 0.04)) 100%
              ),
              linear-gradient(
                0deg,
                var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04))
                  0%,
                var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04))
                  100%
              ),
              var(--surface-primary, #080808);
          }
        `
      case 'tertiary-invert':
        return css`
          display: inline-flex;
          padding: var(--units-12, 12px) var(--units-20, 20px);
          justify-content: center;
          align-items: center;
          gap: var(--units-8, 8px);
          border-radius: 48px;
          background: linear-gradient(
              0deg,
              var(--surface-minimal-alpha-primary, rgba(0, 0, 0, 0.04)) 0%,
              var(--surface-minimal-alpha-primary, rgba(0, 0, 0, 0.04)) 100%
            ),
            #ff0;
          color: #080808;
          text-align: center;
          font-family: var(--type-family-space-grotesk, 'Space Grotesk');
          font-size: var(--Text-M-M-size, 14px);
          font-style: normal;
          font-weight: var(--type-weight-Semibold, 600);
          line-height: var(--Text-M-M-line-height, 18px); /* 128.571% */
          letter-spacing: -0.28px;
          border: none;
          :hover {
            background: linear-gradient(
                0deg,
                var(--surface-minimal-alpha-primary, rgba(0, 0, 0, 0.06)) 0%,
                var(--surface-minimal-alpha-primary, rgba(0, 0, 0, 0.06)) 100%
              ),
              #ff0;
          }
        `
      case 'active-chip':
        return css`
          background: transparent;
          border-radius: 88px;
          border: 1px solid ${({ theme }) => theme.colors.brandPrimary};
          :hover {
            background: ${({ theme }) => theme.colors.brandPrimary};
            color: ${({ theme }) => theme.semantics.zap.text.invert};
            img {
              filter: saturate(0) brightness(0);
            }
          }
        `
      case 'surface-chip':
        return css`
          border-radius: 88px;
          background: ${({ theme }) =>
            theme.semantics.zap.surface.minimalPrimary};
          color: ${({ theme }) => theme.semantics.zap.text.primary};
          gap: var(
            --units-8,
            8px
          ); // if you remove this, check where we use a button with an image (like SwitchToBlastButton)
          :hover {
            background: ${({ theme }) =>
              theme.semantics.zap.surface.minimalSecondary};
            color: white;
            img {
              filter: saturate(1);
            }
          }
        `
      case 'surface-chip-disabled':
        return css`
          border-radius: 88px;
          background: ${({ theme }) =>
            theme.semantics.zap.surface.minimalPrimary};
          color: ${({ theme }) => theme.semantics.zap.text.primary};
          :hover {
            background: ${({ theme }) =>
              theme.semantics.zap.surface.minimalSecondary};
            color: white;
            img {
              filter: saturate(1);
            }
          }
          opacity: 0.5;
        `
      case 'surface-chip-default-text':
        return css`
          border-radius: 88px;
          background: ${({ theme }) =>
            theme.semantics.zap.surface.minimalPrimary};
          color: var(--text-primary, #fff);
          text-align: center;

          /* Web/Text/M/Semibold */
          font-family: var(--type-family-space-grotesk, 'Space Grotesk');
          font-size: var(--Text-M-M-size, 14px);
          font-style: normal;
          font-weight: var(--type-weight-Semibold, 600);
          line-height: var(--Text-M-M-line-height, 18px); /* 128.571% */
          letter-spacing: -0.28px;
          :hover {
            background: ${({ theme }) =>
              theme.semantics.zap.surface.minimalSecondary};
            color: white;
            img {
              filter: saturate(1);
            }
          }
        `
      default:
        return css`
          color: white;
          background-color: transparent;
          border: 1px solid grey;
          --button-border-color: grey;
          :hover {
            color: black;
            background: ${({ theme }) => theme.colors.brandPrimary};
            border: 1px solid ${({ theme }) => theme.colors.brandPrimary};
            filter: brightness(0.75);
            --button-border-color: ${theme.colors.brandPrimary};
          }
          :disabled {
            color: rgba(255, 255, 255, 0.2);
            background: transparent;
            border-color: rgba(255, 255, 255, 0.2);
            --button-border-color: rgba(255, 255, 255, 0.2);
          }
        `
    }
  }}
  :hover {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  }
  ${({ noHover }) =>
    noHover &&
    css`
      transition: none;
      :hover {
        cursor: default;
        border-color: unset;
        background: unset;
      }
    `}
`

export type ButtonProps = ThemedButtonProps & BarrySurfaceWithStateProps

export const StyledThemedButton = styled.button<ButtonProps>`
  ${barrySurface}
  ${buttonStyles}
  ${({ sp }) => sp && paddingRules}
  ${customPadding}
  ${defaultTransition}
`

export const StyledThemedLink = styled(Link)<ThemedButtonProps>`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  text-align: center;

  ${buttonStyles}
`

export const StyledThemedAnchor = styled.a<ThemedLinkProps>`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  text-align: center;
  justify-content: center;

  ${buttonStyles}

  ${({ disabled }) =>
    disabled &&
    css`
      filter: saturate(0);
    `}
`
